import { get } from 'svelte/store';
import { format } from 'svelte-i18n';
//Helpers
import { postReq } from 'src/services/http';
import { deepCloneObj } from './chart_helper';
import { user_settings } from 'src/stores/account/user';
import { devices_settings } from 'src/stores/device-runtime/status';
import { devices } from 'src/stores/lists/devices';
import { dateFormat } from 'src/helpers/date_helper';
import { isProAnyEM } from 'src/helpers/interface_helpers';
import CacheStatisticRequest from './chart_cache_helper';
import { convertToUnit, convertToValueAndUnit } from 'src/helpers/units_helpers';
import { compareFWVersions } from 'src/helpers/firmware_compare_helpers';
//Types
import type { SelectOptionPropsType } from '@Shelly/shelly-design-system/index';
import type {
	GetStatsResponseData,
	GetStatsResponseHistoryData,
	RequestStatisticData,
} from 'src/types/requests/charts/get_stats_period';
import type { ChartConfigSeriesData, ConsumptionDataset } from 'src/types/Chart';
import type { Device } from 'src/types/Device';

type netMeteringPeriodsSelectOptionsTypes = SelectOptionPropsType & {
	value: number;
};

export const getNetMeteringPeriods = (): netMeteringPeriodsSelectOptionsTypes[] => {
	const $format = get(format);
	return [
		{
			value: 0,
			title: $format('net_metering_period_0', { default: 'None' }),
		},
		{
			value: 5,
			title: $format('net_metering_period_5', { default: '5min' }),
		},
		{
			value: 15,
			title: $format('net_metering_period_15', { default: '15min' }),
		},
		{
			value: 30,
			title: $format('net_metering_period_30', { default: '30min' }),
		},
		{
			value: 60,
			title: $format('net_metering_period_60', { default: 'Hour' }),
		},
	];
};

export const getNetMetering = async (
	reqURL: string,
	reqData: RequestStatisticData,
	resData: GetStatsResponseData,
	server: string,
	isThreeEMTriphase: boolean = false,
): Promise<GetStatsResponseData> => {
	const netMeteringRes = await CacheStatisticRequest.getData(reqURL, reqData, server);
	if (!netMeteringRes?.error && netMeteringRes?.history) {
		const netMeteringResData: GetStatsResponseData = netMeteringRes?.history
			? netMeteringRes
			: netMeteringRes.data;
		resData = addNetMetering(resData, netMeteringResData, isThreeEMTriphase);
	}
	return resData;
};

const addNetMetering = (
	resData: GetStatsResponseData,
	netMeteringResData: GetStatsResponseData,
	isThreeEMTriphase: boolean = false,
): GetStatsResponseData => {
	if (!netMeteringResData?.history?.length) {
		return { history: [] };
	}
	resData = deepCloneObj(resData);
	if (isThreeEMTriphase) {
		for (let i = 0; i < resData.history.length; i++) {
			//@ts-ignore
			resData.history[i] = addNetMeteringByChannel(
				//@ts-ignore
				resData.history[i],
				netMeteringResData.history[i],
			);
		}
		resData.sum = addNetMeteringByChannel(resData.sum, netMeteringResData.sum);
	} else {
		resData.history = addNetMeteringByChannel(resData.history, netMeteringResData.history);
	}
	return resData;
};

const addNetMeteringByChannel = (
	resHistory: GetStatsResponseHistoryData[],
	netMeteringHistory: GetStatsResponseHistoryData[],
) => {
	const dateFormating = 'Y-m-dTH:i:s';
	for (let i = 0; i < resHistory.length; i++) {
		const historyItem: GetStatsResponseHistoryData = resHistory[i];
		const datetime: string = dateFormat(historyItem.datetime, dateFormating);
		if (netMeteringHistory?.find && !historyItem?.missing) {
			const netMeteringItem: GetStatsResponseHistoryData = netMeteringHistory.find(
				(i) => dateFormat(i.datetime, dateFormating) == datetime,
			);
			if (!netMeteringItem) {
				resHistory[i] = { datetime: resHistory[i].datetime, missing: true };
				continue;
			}
			resHistory[i].active_net = netMeteringItem?.active_net || 0;
		}
	}
	return resHistory;
};

export const calculateNetMetering = (consumptionDataset: ConsumptionDataset) => {
	const dataset = Object.keys(consumptionDataset);
	if (!dataset?.length) return consumptionDataset;

	for (const key of dataset) {
		const item: ChartConfigSeriesData = consumptionDataset[key];
		if (item?.missing || item?.active_net == null) continue;

		const netValue = item.active_net;
		if (item.reversed) {
			item.value = netValue < 0 ? netValue : 0;
			continue;
		}

		const originalConsumption = item.value;
		item.value = Math.max(netValue, 0);
		if (originalConsumption > 0) {
			const costPerUnit = (1000 / originalConsumption) * (item.cost || 0);
			item.cost = (item.value / 1000) * costPerUnit;
			for (const meta of item?.metaData || []) {
				if (!meta?.tariff) continue;
				const scaleRatio = item.value / originalConsumption;
				const scaledValue = meta.tariff.value * scaleRatio;
				const valueAndUnit = convertToValueAndUnit(scaledValue, meta.tariff.unit);
				meta.tariff.value = valueAndUnit.value;
				meta.value = `${valueAndUnit.value} ${valueAndUnit.unit}`;
				meta.tariff.cost = (scaledValue / 1000) * costPerUnit;
				meta.cost = convertToUnit(meta.tariff.cost, meta.tariff.currency, {
					fixed: 2,
					dontConvert: true,
				});
			}
		}
	}
	return consumptionDataset;
};

export const isExperimentalNetMeteringEnabled = (): boolean => {
	return get(user_settings)?.experimental_features?.nm?.enabled === 1;
};

export const disableAllNetMetering = async () => {
	const allDevices: { [key: string]: Device } = get(devices);
	Object.entries(allDevices).forEach(async ([key, device]) => {
		if (isProAnyEM(device?.type) && device?.cloud_options?.measure_net_energy > 0) {
			const res = await postReq('interface/device/set_cloud_options', {
				id: key,
				set: 'measure_net_energy',
				val: 0,
			});
			if (res?.isok) {
				device.cloud_options.measure_net_energy = 0;
				devices.update(device);
			}
		}
	});
};

export const isSupportNetMetering = (device: Device) => {
	return isProAnyEM(device?.type) && isSupportFWNetMetering(device);
};

export const isSupportFWNetMetering = (device: Device) => {
	const settings = devices_settings.getByRealId(device?.id);
	const fwVersion = settings?.DeviceInfo?.ver || settings?.sys?.device?.fw_id || '0.0.1';
	return compareFWVersions(fwVersion, '>=', '1.5.0');
};
